/**
 * AtlasChatModule — proximity player chat (2026-07-21), bottom-left, for
 * EVERYONE in Cosmos (not admin-gated).
 *
 *  · 'say' messages are heard only within CHAT_RADIUS_M of where the sender
 *    STOOD WHEN SENDING (the server stamps the position; distance is checked
 *    against the stamp, so walking toward a 10-second-old message can't
 *    retroactively "hear" it).
 *  · /w <name> <message> whispers ONE player world-wide (client-side display
 *    filter — the table is public, like every Atlas social table).
 *  · Admins additionally get the debug levers as slash commands (/tp /tpv
 *    /weather /time /versonas) — the same admin-gated reducers as the debug
 *    window, so the client gate is UX only; the server re-checks caps.
 *
 * Input: Enter focuses the box (ignored while any other input/textarea has
 * focus — AtlasInput's own guard keeps WASD from firing while typing), Escape
 * blurs, Enter sends. Subscription is per-world (re-subscribed on world entry,
 * deduped by worldId); the server prunes the log to 100 rows per world.
 */
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { capsIn } from '../lib/world_caps.js';
import { hiddenFrom, myParty, myInvites, routeAnswer, type AnswerCmd } from '../lib/social_context.js';
import { pendingInvite as pendingDuelInvite, myDuel, myMember, matchOf, myQueue } from '../lib/pvp_context.js';
import type { DbConnection } from '../module_bindings/index.js';
import { wireChatTranslate, translateLine, translateOn, setTranslateOn, myChatLang, setMyChatLang, langName, supportedLangs, chatTranslateState } from '../lib/chat_translate.js';

const CHAT_RADIUS_M = 80;      // hearing range for 'say'
const SHOW_MAX = 60;           // DOM rows kept
const HISTORY_ON_JOIN = 10;    // recent in-range rows shown when entering

// xr-ui (2026-09-28): DOM never renders in a headset, so the VR panel's Chat page reads the lines this module SHOWED
// (after its range / mute / whisper rules) from this ring instead of re-deriving them.
const XR_CHAT_RING: Array<{ who: string; text: string; color: string }> = [];
let xrChatVersion = 0;
/** The last `n` lines as shown (oldest first; `who` '' for a system line; `color` a CSS value) + a per-line version. */
export function recentChatLines(n = 40): { version: number; lines: ReadonlyArray<{ who: string; text: string; color: string }> } {
  return { version: xrChatVersion, lines: XR_CHAT_RING.slice(-Math.max(0, n)) };
}
function xrChatPush(who: string, text: string, color: string): void {
  XR_CHAT_RING.push({ who, text, color });
  if (XR_CHAT_RING.length > SHOW_MAX) XR_CHAT_RING.splice(0, XR_CHAT_RING.length - SHOW_MAX);
  xrChatVersion++;
}

interface ChatRow {
  msgId: bigint; worldId: bigint; sender: { toHexString(): string }; senderName: string;
  kind: string; target: { toHexString(): string }; posX: number; posZ: number; text: string;
}

export class AtlasChatModule implements IModule {
  private nonce = 0;
  private layoutTimer: number | null = null;
  readonly name = 'AtlasChat';
  private root: HTMLDivElement | null = null;
  private list: HTMLDivElement | null = null;
  private input: HTMLInputElement | null = null;
  private subscribedWorldId: bigint | null = null;
  private chatSub: { unsubscribe(): void } | null = null;
  private wired = false;
  private seen = new Set<string>(); // msgIds rendered (join-history + live overlap)

  private onKey = (e: KeyboardEvent): void => {
    if (e.key !== 'Enter' || !this.input) return;
    const a = document.activeElement;
    if (a && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA')) return; // some other UI owns typing
    if (atlas.currentWorldId === null) return;
    e.preventDefault();
    this.input.focus();
  };

  init(_app: App): void {
    this.buildDom();
    window.addEventListener('keydown', this.onKey);
    bus.on('atlas:account-ready', () => this.wire());
    bus.on('atlas:world-entered', () => { this.resubscribe(); });
  }

  private buildDom(): void {
    const root = document.createElement('div');
    root.id = 'atlas-chat';
    root.style.cssText = 'position:fixed;left:12px;bottom:88px;width:360px;z-index:40;' // above the menu bar — at ≤1440 px the centered bar's left edge underlapped the chat
      + 'font:12px/1.45 var(--ax-mono);color:var(--ax-text);pointer-events:none;';
    // HUD ladder v2 (2026-08-24): in space the ship-status panel owns the
    // bottom-left corner (220 px wide, ~120 tall) — ride above it.
    let chatInSpace = false;
    this.layoutTimer = window.setInterval(() => {
      if (atlas.spaceFlight === chatInSpace) return;
      chatInSpace = atlas.spaceFlight;
      root.style.bottom = chatInSpace ? '150px' : '88px';
    }, 500);
    const list = document.createElement('div');
    list.style.cssText = 'max-height:176px;overflow-y:auto;display:flex;flex-direction:column;gap:2px;'
      + 'padding:6px 8px;border-radius:8px 8px 0 0;background:color-mix(in srgb, var(--ax-bg) 55%, transparent);pointer-events:auto;'
      + 'scrollbar-width:thin;overflow-wrap:anywhere;';
    const input = document.createElement('input');
    input.type = 'text';
    input.maxLength = 240;
    input.placeholder = 'Press Enter to chat — /help for commands';
    input.style.cssText = 'width:100%;box-sizing:border-box;padding:6px 8px;border:0;outline:none;'
      + 'border-radius:0 0 8px 8px;background:color-mix(in srgb, var(--ax-bg) 70%, transparent);color:var(--ax-text);pointer-events:auto;'
      + 'font:12px var(--ax-mono);';
    input.addEventListener('keydown', (e) => {
      e.stopPropagation(); // never leaks to game input
      if (e.key === 'Escape') { input.blur(); }
      if (e.key === 'Enter') {
        const text = input.value.trim();
        input.value = '';
        input.blur();
        if (text) this.submit(text);
      }
    });
    root.appendChild(list);
    root.appendChild(input);
    document.body.appendChild(root);
    this.root = root; this.list = list; this.input = input;
    this.appendLocal('· proximity chat — heard within 80 m · /w name message to whisper', 'var(--ax-muted)');
  }

  // ── Wiring ────────────────────────────────────────────────────────────────
  private wire(): void {
    const conn = atlas.conn;
    if (!conn || this.wired) return;
    this.wired = true;
    conn.db.chatMessage.onInsert((_ctx, row) => this.onRow(conn, row as unknown as ChatRow, true));
    // Whispers are PRIVATE rows now (direct_message, row-level secured); the Social module relays them here.
    bus.on('atlas:chat-line', ({ who, text, color }) => this.append(who, text, color));
    // LIVE CHAT TRANSLATION (2026-09-24): my declared language, the cache in it, the on-device translator first.
    wireChatTranslate();
    (window as unknown as { __chatTranslate?: unknown }).__chatTranslate = { state: chatTranslateState, lang: myChatLang };
    this.resubscribe();
  }

  private resubscribe(): void {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null || this.subscribedWorldId === wid) return;
    this.subscribedWorldId = wid;
    // (bug sweep) every world visited stacked another live chat_message
    // window — keep the handle; release the previous world's once this one
    // has applied (subscribe-new-then-unsubscribe-old, no row-cache gap).
    const prev = this.chatSub;
    this.chatSub = conn.subscriptionBuilder().onApplied(() => {
      try { prev?.unsubscribe(); } catch { /* gone */ }
      // Join history — deferred until the SELF position has mirrored. The
      // radius filter otherwise compares against the (0,0) defaults, letting
      // any (0,0)-stamped backlog through (CLI/probe messages surfaced this).
      const replay = (): void => {
        if (this.subscribedWorldId !== wid) return; // hopped worlds meanwhile
        if (!this.selfPos(conn)) { window.setTimeout(replay, 250); return; }
        const rows = [...conn.db.chatMessage.iter()] as unknown as ChatRow[];
        rows.sort((a, b) => (a.msgId < b.msgId ? -1 : 1));
        for (const r of rows.slice(-HISTORY_ON_JOIN)) this.onRow(conn, r, false);
      };
      replay();
    }).subscribe([`SELECT * FROM chat_message WHERE world_id = ${wid}`]);
  }

  /** The listener's authoritative position (self player row), or null pre-mirror. */
  private selfPos(conn: DbConnection): { x: number; z: number } | null {
    const meHex = atlas.identityHex;
    if (meHex === null) return null;
    for (const p of conn.db.playerState.iter()) {
      if (p.identity.toHexString() === meHex) return { x: p.posX, z: p.posZ };
    }
    return null;
  }

  // ── Receive ───────────────────────────────────────────────────────────────
  private onRow(conn: DbConnection, row: ChatRow, live: boolean): void {
    if (row.worldId !== atlas.currentWorldId) return;
    const id = String(row.msgId);
    if (this.seen.has(id)) return;
    const meHex = atlas.identityHex;
    const mine = meHex !== null && row.sender.toHexString() === meHex;
    if (row.kind === 'whisper') {
      const toMe = meHex !== null && row.target.toHexString() === meHex;
      if (toMe) this.append(`${row.senderName} → you`, row.text, 'var(--ax-rare)');
      else if (mine && live) this.append('you (whisper)', row.text, 'var(--ax-rare)');
      this.seen.add(id);
      return;
    }
    // 'say': my own always shows; others only within range of the SEND stamp — and never a muted voice.
    if (!mine && hiddenFrom(row.sender.toHexString())) { this.seen.add(id); return; }
    if (!mine) {
      const me = this.selfPos(conn) ?? { x: atlas.localX, z: atlas.localZ };
      const d = Math.hypot(row.posX - me.x, row.posZ - me.z);
      if (d > CHAT_RADIUS_M) return;
    }
    this.seen.add(id);
    const line = this.append(mine ? 'You' : row.senderName, row.text, mine ? 'var(--ax-good)' : 'var(--ax-text)');
    if (!mine && line) this.translateInto(line, row);
  }

  /** A heard line in another language gets its translation underneath (click the line to see the original). */
  private translateInto(line: HTMLDivElement, row: ChatRow): void {
    if (!translateOn()) return;
    void translateLine(row.msgId, row.text, row.sender.toHexString()).then((t) => {
      if (!t || !line.isConnected) return;
      const sub = document.createElement('div');
      sub.textContent = `↳ ${t.text}`;
      sub.title = `Translated from ${langName(t.from)} (${t.engine === 'on-device' ? 'on this device' : 'shared translation'}) — click to show the original`;
      sub.style.cssText = 'color:var(--ax-soft);padding-left:10px;cursor:pointer';
      sub.dataset.translated = t.from;
      let showing = true;
      sub.addEventListener('click', () => { showing = !showing; sub.textContent = showing ? `↳ ${t.text}` : `↳ (${langName(t.from)}) ${row.text}`; });
      line.appendChild(sub);
      const l = this.list;
      if (l && l.scrollHeight - l.scrollTop - l.clientHeight < 60) l.scrollTop = l.scrollHeight;
    });
  }

  // ── Send + commands ───────────────────────────────────────────────────────
  private submit(text: string): void {
    const conn = atlas.conn;
    if (!conn || atlas.currentWorldId === null) return;
    if (!text.startsWith('/')) {
      void conn.reducers.sendChat({ text }).catch((e: Error) => this.appendLocal(`· ${e.message}`, 'var(--ax-bad)'));
      return;
    }
    const [cmd, ...rest] = text.slice(1).split(/\s+/);
    const wid = atlas.currentWorldId;
    const admin = capsIn(wid).admin;
    const fail = (m: string): void => this.appendLocal(`· ${m}`, 'var(--ax-bad)');
    switch (cmd.toLowerCase()) {
      case 'w': case 'whisper': {
        const [name, ...msg] = rest;
        if (!name || msg.length === 0) return fail('usage: /w <name> <message>');
        void conn.reducers.sendWhisper({ targetUsername: name, text: msg.join(' '), nonce: (this.nonce = (this.nonce % 4_000_000) + 1) })
          .then(() => this.append(`you → ${name}`, msg.join(' '), 'var(--ax-rare)'))
          .catch((e: Error) => fail(e.message));
        return;
      }
      case 'translate': {
        const arg = (rest[0] ?? '').toLowerCase();
        if (arg === 'off') { setTranslateOn(false); this.appendLocal('· translation off — lines show as written', 'var(--ax-muted)'); return; }
        if (arg === 'on') { setTranslateOn(true); this.appendLocal(`· translation on — into ${langName(myChatLang())}`, 'var(--ax-muted)'); return; }
        if (arg && setMyChatLang(arg)) { setTranslateOn(true); wireChatTranslate(); this.appendLocal(`· you read and write ${langName(arg)} — other languages are translated into it`, 'var(--ax-muted)'); return; }
        if (arg) return fail(`unknown language "${arg}" — one of ${supportedLangs().join(' ')}`);
        this.appendLocal(`· translation ${translateOn() ? 'on' : 'off'} · your language ${langName(myChatLang())} (${myChatLang()}) · /translate on|off|<code>`, 'var(--ax-muted)');
        return;
      }
      case 'help': {
        this.appendLocal('· /w <name> <msg> — whisper one player (world-wide)', 'var(--ax-muted)');
        this.appendLocal('· /translate on|off|<lang> — live translation of what others say (your language: ' + myChatLang() + ')', 'var(--ax-muted)');
        this.appendLocal('· /family [versonaId] — a townsperson\'s kin (nearest versona if omitted)', 'var(--ax-muted)');
        this.appendLocal('· /duel [name] · /accept · /decline · /ready · /withdraw · /yield — a duel at the settlement\'s sparring ring', 'var(--ax-muted)');
        this.appendLocal('· /party · /invite <name> · /accept · /decline · /leave · /ready (these follow whichever is live: a duel, a match, a party) · /p <text> · /say <phrase> · /ping <kind> — a party (G opens Social)', 'var(--ax-muted)');
        this.appendLocal('· /friend <name> · /block · /unblock · /mute · /unmute · /report <name> <category> · /trade <name>', 'var(--ax-muted)');
        this.appendLocal('· /arena [1-4] · /leavequeue · /leave — Relay Skirmish; /enroll [side] · /disengage — a conflict sector; /pvp — status', 'var(--ax-muted)');
        if (admin) this.appendLocal('· admin: /tp <x> <z> · /tpv [versonaId] · /versonas [filter] · /weather <kind> · /time <0..1>', 'var(--ax-muted)');
        return;
      }
      case 'family': {
        this.familyCmd(conn, rest[0]);
        return;
      }
      // Ground PvP (2026-09-18): the PvP module owns these; the chat only relays.
      case 'duel': case 'yield': case 'arena': case 'leavequeue': case 'enroll': case 'disengage': case 'pvp': {
        bus.emit('atlas:pvp-cmd', { cmd: cmd.toLowerCase(), args: rest });
        return;
      }
      case 'withdraw': { bus.emit('atlas:pvp-cmd', { cmd: 'cancel', args: rest }); return; }
      // AMBIGUOUS BY DESIGN (2026-09-18): accept / decline / ready / leave mean
      // one thing in a duel or a match and another in a party. Route by what is
      // actually live — a shared `case` list silently swallowed every party
      // answer, so /accept could never take a party invitation.
      case 'accept': case 'decline': case 'ready': case 'leave': {
        const c = cmd.toLowerCase() as AnswerCmd;
        const mem = myMember();
        const match = mem ? matchOf(mem.matchId) : null;
        const where = routeAnswer(c, {
          duelInvite: !!pendingDuelInvite(), duelState: myDuel()?.state ?? null, matchState: match?.state ?? null,
          inMatch: !!mem, queued: !!myQueue(), partyInvites: myInvites().length, inParty: !!myParty(),
        });
        bus.emit(where === 'social' ? 'atlas:social-cmd' : 'atlas:pvp-cmd', { cmd: c, args: rest });
        return;
      }
      // Player-to-player (2026-09-18): the Social module owns these; the chat only relays.
      case 'party': case 'invite': case 'kick': case 'lead': case 'p': case 'say': case 'ping': case 'ack':
      case 'queue': case 'enqueue': case 'friend': case 'unfriend': case 'block': case 'unblock': case 'mute': case 'unmute': case 'report': case 'trade': case 'tradeaccept': case 'tradecancel': {
        bus.emit('atlas:social-cmd', { cmd: cmd.toLowerCase(), args: rest });
        return;
      }
      case 'tp': {
        if (!admin) return fail('admin only');
        const [x, z] = rest.map(Number);
        if (!Number.isFinite(x) || !Number.isFinite(z)) return fail('usage: /tp <x> <z>');
        void conn.reducers.debugTeleport({ posX: x, posZ: z }).catch((e: Error) => fail(e.message));
        return;
      }
      case 'tpv': {
        if (!admin) return fail('admin only');
        void conn.reducers.debugTeleportToVersona({ versonaId: BigInt(rest[0] ?? 0) })
          .catch((e: Error) => fail(e.message));
        return;
      }
      case 'versonas': {
        if (!admin) return fail('admin only');
        const f = (rest[0] ?? '').toLowerCase();
        const rows: Array<{ id: bigint; a: string; d: number }> = [];
        for (const v of conn.db.versona.iter()) {
          if (f && !v.archetype.toLowerCase().includes(f)) continue;
          rows.push({ id: v.versonaId, a: v.archetype, d: Math.round(Math.hypot(v.curX - atlas.localX, v.curZ - atlas.localZ)) });
        }
        rows.sort((a, b) => a.d - b.d);
        for (const r of rows.slice(0, 8)) this.appendLocal(`· #${r.id} ${r.a} — ${r.d} m`, 'var(--ax-muted)');
        this.appendLocal(`· ${rows.length} versonas${f ? ` matching "${f}"` : ''} (nearest 8; /tpv <id> to visit)`, 'var(--ax-muted)');
        return;
      }
      case 'weather': {
        if (!admin) return fail('admin only');
        if (!rest[0]) return fail('usage: /weather <clear|rain|snow|…>');
        void conn.reducers.setWeather({ worldId: wid, weather: rest[0] }).catch((e: Error) => fail(e.message));
        return;
      }
      case 'time': {
        if (!admin) return fail('admin only');
        const tod = Number(rest[0]);
        if (!Number.isFinite(tod) || tod < 0 || tod > 1) return fail('usage: /time <0..1> (0.5 = noon)');
        void conn.reducers.debugSetSkyTime({ worldId: wid, timeOfDay: tod }).catch((e: Error) => fail(e.message));
        return;
      }
      default: return fail(`unknown command /${cmd} — /help`);
    }
  }

  // ── DOM helpers ───────────────────────────────────────────────────────────
  private append(who: string, text: string, color: string): HTMLDivElement | null {
    if (!this.list) return null;
    const line = document.createElement('div');
    const name = document.createElement('span');
    name.textContent = `${who}: `;
    name.style.cssText = `color:${color};font-weight:600;`;
    line.appendChild(name);
    line.appendChild(document.createTextNode(text));
    this.push(line);
    xrChatPush(who, text, color); // xr-ui (2026-09-28): the VR Chat page's copy
    return line;
  }

  private appendLocal(text: string, color: string): void {
    if (!this.list) return;
    const line = document.createElement('div');
    line.textContent = text;
    line.style.color = color;
    this.push(line);
    xrChatPush('', text, color); // xr-ui (2026-09-28): the VR Chat page's copy
  }

  private push(line: HTMLDivElement): void {
    const l = this.list!;
    l.appendChild(line);
    while (l.children.length > SHOW_MAX) l.removeChild(l.firstChild!);
    l.scrollTop = l.scrollHeight;
  }

  // ── /family — a townsperson's kin, from the replicated society tables ──────
  private familySubscribed = false;

  private familyCmd(conn: DbConnection, arg?: string): void {
    // Lazy one-off subscription: the society tables are small and cold, so
    // they join the cache only once someone actually asks about a family.
    if (!this.familySubscribed) {
      this.familySubscribed = true;
      conn.subscriptionBuilder().onApplied(() => this.familyCmd(conn, arg)).subscribe([
        'SELECT * FROM versona_lineage', 'SELECT * FROM kinship',
        'SELECT * FROM household', 'SELECT * FROM household_member', 'SELECT * FROM npc_agent',
      ]);
      this.appendLocal('· consulting the town records…', 'var(--ax-muted)');
      return;
    }
    let id = 0n;
    if (arg) {
      try { id = BigInt(arg); } catch { /* fall through to nearest */ }
    }
    if (id === 0n) {
      let bd = 30 * 30;
      for (const v of conn.db.versona.iter()) {
        const d = (v.curX - atlas.localX) ** 2 + (v.curZ - atlas.localZ) ** 2;
        if (d < bd) { bd = d; id = v.versonaId; }
      }
      if (id === 0n) { this.appendLocal('· nobody close enough — stand near a versona or /family <id>', 'var(--ax-bad)'); return; }
    }
    const jobOf = (vid: bigint): string => {
      for (const a of conn.db.npcAgent.iter()) if (a.versonaId === vid) return a.job;
      return 'settler';
    };
    const name = (vid: bigint): string => `the ${jobOf(vid)} (#${vid})`;
    const lines: string[] = [];
    let surname = '';
    for (const m of conn.db.householdMember.iter()) {
      if (m.versonaId !== id) continue;
      for (const h of conn.db.household.iter()) if (h.householdId === m.householdId) surname = h.surname;
    }
    lines.push(`${name(id)}${surname ? ` — the ${surname} household` : ''}`);
    for (const l of conn.db.versonaLineage.iter()) {
      if (l.childId === id) lines.push(`  born of ${name(l.parentA)} and ${name(l.parentB)} (generation ${l.generation})`);
    }
    const kids: string[] = [];
    for (const l of conn.db.versonaLineage.iter()) {
      if (l.parentA === id || l.parentB === id) kids.push(name(l.childId));
    }
    if (kids.length > 0) lines.push(`  children: ${kids.slice(0, 4).join(', ')}`);
    for (const k of conn.db.kinship.iter()) {
      if (k.kind === 'partner' && (k.aId === id || k.bId === id)) lines.push(`  partnered to ${name(k.aId === id ? k.bId : k.aId)}`);
      if (k.kind === 'mentor' && k.bId === id) lines.push(`  apprenticed under ${name(k.aId)}`);
    }
    if (lines.length === 1) lines.push('  no recorded kin — a founder of the town, or new to it');
    for (const l of lines) this.appendLocal(`· ${l}`, 'var(--ax-soft)');
  }

  dispose(): void {
    if (this.layoutTimer !== null) { window.clearInterval(this.layoutTimer); this.layoutTimer = null; } // (bug sweep) the layout poller ran forever
    window.removeEventListener('keydown', this.onKey);
    this.root?.remove();
  }
}
